import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '@/config/api';

const FIELDS = [
  'class_reminder_enabled', 'class_reminder_sms', 'class_reminder_email', 'class_reminder_sms_template',
  'class_reminder_email_subject', 'class_reminder_email_template',
  'absence_message_enabled', 'absence_sms', 'absence_email', 'absence_sms_template',
  'absence_email_subject', 'absence_email_template',
];

const Toggle = ({ label, checked, onChange }) => (
  <label className="flex items-center justify-between gap-3 rounded-xl bg-slate-50 px-3 py-2.5 cursor-pointer">
    <span className="text-sm text-slate-700">{label}</span>
    <Switch checked={!!checked} onCheckedChange={onChange} />
  </label>
);

const Section = ({ title, prefix, form, set }) => (
  <div className="space-y-2">
    <p className="text-sm font-semibold text-slate-900">{title}</p>
    <Toggle label="Switched on" checked={form[`${prefix}_enabled`] ?? form[`${prefix}_message_enabled`]} onChange={(v) => set(prefix === 'absence' ? 'absence_message_enabled' : `${prefix}_enabled`, v)} />
    <div className="grid grid-cols-2 gap-2">
      <Toggle label="Text" checked={form[`${prefix}_sms`]} onChange={(v) => set(`${prefix}_sms`, v)} />
      <Toggle label="Email" checked={form[`${prefix}_email`]} onChange={(v) => set(`${prefix}_email`, v)} />
    </div>
    {form[`${prefix}_sms`] && (
      <div>
        <label className="text-xs font-medium text-slate-600 mb-1 block">Text message</label>
        <Textarea rows={3} value={form[`${prefix}_sms_template`] || ''} onChange={(e) => set(`${prefix}_sms_template`, e.target.value)} className="rounded-xl text-sm" />
      </div>
    )}
    {form[`${prefix}_email`] && (
      <div className="space-y-2">
        <label className="text-xs font-medium text-slate-600 block">Email</label>
        <Input value={form[`${prefix}_email_subject`] || ''} onChange={(e) => set(`${prefix}_email_subject`, e.target.value)} className="rounded-xl text-sm" aria-label="Email subject" />
        <Textarea rows={6} value={form[`${prefix}_email_template`] || ''} onChange={(e) => set(`${prefix}_email_template`, e.target.value)} className="rounded-xl text-sm" aria-label="Email body" />
      </div>
    )}
  </div>
);

const MessageSettingsDialog = ({ open, onOpenChange }) => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (!open) return;
    setForm(null);
    fetch(`${API_URL}/api/training/growth-settings`, { headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include' })
      .then(r => (r.ok ? r.json() : null))
      .then(s => s && setForm(Object.fromEntries(FIELDS.map(f => [f, s[f]]))))
      .catch(() => toast.error('Failed to load message settings'));
  }, [open, token]);

  const set = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/training/growth-settings`, {
        method: 'PUT', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify(form),
      });
      if (res.ok) { toast.success('Message settings saved'); onOpenChange(false); }
      else { const e = await res.json().catch(() => ({})); toast.error(e.detail || 'Failed to save'); }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Student messages</DialogTitle>
          <DialogDescription>
            Sent from your GHL number and email. You can use {'{first_name}'}, {'{course}'}, {'{date}'}, {'{time}'}, {'{end_time}'}, {'{location_text}'}, {'{centre}'}.
          </DialogDescription>
        </DialogHeader>
        {!form ? (
          <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 text-pink-400 animate-spin" /></div>
        ) : (
          <div className="space-y-6 py-1">
            <Section title="Class reminder — the day before, at 5pm" prefix="class_reminder" form={form} set={set} />
            <Section title='"We missed you" — when a student is marked absent' prefix="absence" form={form} set={set} />
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" className="rounded-xl" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="btn-primary rounded-xl" onClick={save} disabled={saving || !form}>
            {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MessageSettingsDialog;
